// ----------------------------------------------------------------------------
// Helper: Loyalty Pricing rule matching
// ----------------------------------------------------------------------------
// The single source of truth on the storefront for how a vendor rule maps to a
// discount percentage. This MIRRORS the Discount Function's
// matching logic (lethal-performance-app →
// extensions/loyalty-pricing/src/match-rules.ts) and the server-side Liquid
// snippet (snippets/loyalty-price-percentage.liquid, used by product cards + the
// PDP). Keep all three in sync so the price a shopper sees always matches what
// they pay at checkout.
//
// Config comes from window.loyaltyPricing (set in layout/theme.liquid from the
// loyalty_pricing/rules shop metafield).
// ----------------------------------------------------------------------------

/**
 * Coerce a percentage into a finite 0–100 number (0 ⇒ no discount).
 * @param {*} value
 * @returns {Number}
 */
export function clampPercentage(value) {
  var n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 100 ? 100 : n;
}

/**
 * The effective discount percentage for a product. Every matching rule is
 * considered; the HIGHEST percentage wins. A rule matches when the product
 * vendor equals rule.match (case-insensitive). Falls back to defaultPercentage
 * (which may be 0).
 * @param {Array} rules
 * @param {String|null} vendor
 * @param {Number} defaultPercentage
 * @returns {Number}
 */
export function effectivePercentage(rules, vendor) {
  var defaultPercentage = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var v = (vendor || '').trim().toLowerCase();
  var best = clampPercentage(defaultPercentage);
  for (var rule of rules || []) {
    if (rule && rule.enabled === false) continue;
    var target = (rule && rule.match ? String(rule.match) : '').trim().toLowerCase();
    if (!target) continue;
    if (v !== '' && v === target) {
      var pct = clampPercentage(rule.percentage);
      if (pct > best) best = pct;
    }
  }
  return best;
}

/**
 * Loyalty price in cents, computed to MATCH Shopify checkout exactly.
 *
 * The Discount Function emits a percentage; Shopify then applies it per line by
 * flooring the discount amount to whole cents and subtracting it. So we must do
 * the same — price - floor(price * pct/100) — NOT round(price * (1 - pct/100)),
 * which drifts by a cent. Confirmed against a live cart: $905.99 @ 7% → discount
 * floor($63.4193) = $63.41 → $842.58 (round() gives $842.57, off by 1¢).
 *
 * Keep this in sync with the Liquid renders (loyalty-price-percentage.liquid
 * consumers: product-form__price.liquid + card--product.liquid), which use
 * integer division to floor the same way.
 * @param {Number} priceCents
 * @param {Number} percentage
 * @returns {Number}
 */
export function loyaltyPriceCents(priceCents, percentage) {
  return priceCents - Math.floor(priceCents * percentage / 100);
}

/**
 * Whether a product is EXCLUDED from loyalty pricing by tag. Mirrors the
 * server-side snippets/loyalty-excluded.liquid and the Discount Function's
 * hasAnyTag check: a product carrying any tag listed in the config's
 * `excludeTags` never receives loyalty pricing, even if a vendor rule
 * matches. Comparison is case-insensitive and whitespace-trimmed. Keep in sync.
 * @param {Array<String>|null} tags  the product's tags
 * @returns {Boolean}
 */
export function isExcludedByTags(tags) {
  var config = typeof window !== 'undefined' && window.loyaltyPricing || {};
  var excludeTags = config.excludeTags || [];
  if (!excludeTags.length || !Array.isArray(tags)) return false;
  var normalized = new Set(excludeTags.map(t => String(t || '').trim().toLowerCase()).filter(Boolean));
  if (!normalized.size) return false;
  return tags.some(t => normalized.has(String(t || '').trim().toLowerCase()));
}

/**
 * The effective loyalty discount percentage for the CURRENT shopper, reading the
 * storefront config (window.loyaltyPricing). Returns 0 for logged-out shoppers or
 * non-eligible products, so callers can treat `> 0` as "loyalty price applies".
 * Tag exclusion is intentionally NOT applied here (callers that have the product
 * tags should gate with isExcludedByTags first) — this mirrors the server-side
 * split between loyalty-price-percentage.liquid and loyalty-excluded.liquid.
 * @param {String|null} vendor
 * @returns {Number}
 */
export function loyaltyFactorFor(vendor) {
  var config = typeof window !== 'undefined' && window.loyaltyPricing || {};
  if (!config.loggedIn) return 0;
  return effectivePercentage(config.rules, vendor, config.defaultPercentage);
}